<template>
    <index-table
        :cols="columns"
        :title="'App Repository'"
        :model="'app-repository'"
        :createText="createText"
        :options="options"
        :multiItemActions="[]"
        :optionsComponent="optionsComponent"
        noResultsText="No apps available. Please contact your organization admin for more information."
        data-test-id="app-repo-apps-table"
    />
</template>

<script>
import IndexTable from '@/components/IndexTable/index.vue'
import SortDirections from '@/constants/sort-directions'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import canToast from '@/mixins/canToast'

const baseColumns = [
    {
        value: 'type',
        label: 'Type',
        sortable: true,
        searchable: true,
        filter: 'snakeToTitleCase',
    },
    {
        value: 'display_name',
        label: 'Title',
        sortable: true,
        searchable: true,
    },
    {
        value: 'version',
        label: 'Version',
        sortable: true,
        searchable: true,
    },
    {
        value: 'atak_version',
        label: 'ATAK Vers.',
        sortable: true,
        searchable: true,
    },
    {
        value: 'gotenna_version',
        label: 'goTenna Vers.',
        sortable: true,
        searchable: true,
        customCell: 'GoTennaVersionCell',
    },
    {
        value: 'firmware',
        label: 'Firmware Vers.',
        sortable: false,
        searchable: true,
        display: 'version',
    },
    {
        value: 'release_date',
        label: 'Release Date',
        sortable: true,
        searchable: true,
    },
]

export default {
    components: {
        IndexTable,
    },

    mixins: [needsOrganizationSlug, canToast],

    data() {
        return {
            sortDir: SortDirections.DESC,
            optionsComponent: 'AppRepositoryActions',
        }
    },

    computed: {
        createText() {
            let result = ''

            const { hasPermission } = this.$store.getters

            // only show create new if viewing as super admin and can create an app
            if (!this.organizationSlug && hasPermission('create apps')) {
                result = 'Upload New'
            }

            return result
        },

        options() {
            const result = ['downloadable']
            const { hasPermission } = this.$store.getters

            // only show options if viewing as a super admin
            if (!this.organizationSlug) {
                if (hasPermission('delete apps') && hasPermission('delete many apps')) {
                    result.push('deletable')
                }

                if (hasPermission('update apps')) {
                    result.push('editable')
                }
            }

            return result
        },

        columns() {
            const columns = [...baseColumns]

            const { hasPermission } = this.$store.getters

            if (this.organizationSlug) {
                // viewing app-repo as an org user
                if (hasPermission('manage apps for organization')) {
                    // org admins
                    columns.push({
                        value: 'toggle',
                        label: 'Visible to Users',
                        customCell: 'SliderCell',
                        customProps: { valueName: 'visible_to_organization' },
                        customEvents: {
                            changed: this.visibleToOrganizationChanged,
                        },
                    })
                }
            } else if (hasPermission('update apps')) {
                // viewing app-repo as super admin, not within an org
                columns.push({
                    value: 'toggle',
                    label: 'Visible to Admins',
                    customCell: 'SliderCell',
                    customProps: { valueName: 'visible_to_admins' },
                    customEvents: {
                        changed: this.visibleToAdminsChanged,
                    },
                })
                columns.push({
                    value: 'toggle',
                    label: 'Visible to goKits',
                    customCell: 'SliderCell',
                    customProps: { valueName: 'visible_to_gokits' },
                    customEvents: {
                        changed: this.visibleToGoKitsChanged,
                    },
                })
            }

            return columns
        },
    },

    methods: {
        visibleToAdminsChanged(row, newValue) {
            row.visible_to_admins = Number(newValue)
            const app = {
                ...row,
                firmware_version: row.firmware.map((item) => item.id),
            }

            if (row.is_link) {
                app.link = row.filename
            }

            delete app.firmware

            this.$http
                .put(`/app-repository/${app.id}`, app)
                .then(() => {
                    this.toast('App successfully updated!')
                })
                .catch(() => {
                    this.toast('There was a problem updating the App.', 'error')
                    row.visible_to_admins = Number(!newValue)
                })
        },

        visibleToGoKitsChanged(row, newValue) {
            row.visible_to_gokits = Number(newValue)
            const app = {
                ...row,
                firmware_version: row.firmware.map((item) => item.id),
            }

            if (row.is_link) {
                app.link = row.filename
            }

            delete app.firmware

            this.$http
                .put(`/app-repository/${app.id}`, app)
                .then(() => {
                    this.toast('App successfully updated!')
                })
                .catch(() => {
                    this.toast('There was a problem updating the App.', 'error')
                    row.visible_to_gokits = Number(!newValue)
                })
        },

        visibleToOrganizationChanged(row, newValue) {
            row.visible_to_organization = Number(newValue)
            if (!newValue) {
                this.$http
                    .delete(`${this.organizationSlug}/app-repository/${row.id}`)
                    .then(() => {
                        this.toast('App successfully disabled for users!')
                    })
                    .catch(() => {
                        this.toast('There was a problem updating the App.', 'error')
                        row.visible_to_organization = !newValue
                    })
            } else {
                this.$http
                    .post(`${this.organizationSlug}/app-repository/${row.id}`)
                    .then(() => {
                        this.toast('App successfully enabled for users!')
                    })
                    .catch(() => {
                        this.toast('There was a problem updating the App.', 'error')
                        row.visible_to_organization = !newValue
                    })
            }
        },
    },
}
</script>
